import { OPERATOR_PUBKEY, isOperatorPubkey } from './platform';
import { SPAM_FLAG_THRESHOLD, SPAM_REPORT_WEIGHT, getTrustLevel, type TrustLevel } from './trustScore';

// Kind used for community spam flags
export const FLAG_KIND = 7687;
export const FLAG_TAG = 'bchnostr-flag';

// ── Operator Ban System ─────────────────────────────────────────
// The operator can ban users site-wide. Bans are published as Nostr
// events (kind 7687 with 'bchnostr-ban' tag) so they persist across
// sessions and sync across relays. Banned users' content is hidden
// for ALL visitors, not just the operator.
//
// Event structure:
//   kind: 7687  (same as flags — only operator-authored bans count)
//   tags: [['t', 'bchnostr-ban'], ['p', <banned-pubkey>]]
//   content: <reason>  (optional)
//
// Unbans use the same kind with 'bchnostr-unban' tag.

export const BAN_TAG = 'bchnostr-ban';
export const UNBAN_TAG = 'bchnostr-unban';
const BAN_CACHE_KEY = 'bchnostr:banned-pubkeys';

// ── Global banned set (module-level singleton) ─────────────────
// Loaded from localStorage on init, updated live from relay events.
// Every component can call isBanned() — zero cost, no hooks needed.

let _bannedPubkeys = new Set<string>();
let _banSystemInitialized = false;
let _banListeners: Array<() => void> = [];

function _loadBansFromStorage() {
  try {
    const raw = localStorage.getItem(BAN_CACHE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as string[];
      if (Array.isArray(arr)) arr.forEach(pk => _bannedPubkeys.add(pk));
    }
  } catch {}
}

function _persistBans() {
  try {
    localStorage.setItem(BAN_CACHE_KEY, JSON.stringify([..._bannedPubkeys]));
  } catch {}
}

function _notifyBanListeners() {
  _banListeners.forEach(fn => fn());
}

/** Initialize the ban set from localStorage. Called once at app start. */
export function initBanSystem() {
  if (_banSystemInitialized) return;
  _banSystemInitialized = true;
  _loadBansFromStorage();
}

/**
 * Process ban/unban events from relays. Only events authored by the
 * operator are respected. Call this with all fetched flag events.
 */
export function processBanEvents(events: any[]) {
  let changed = false;
  // Sort by created_at so unbans can override earlier bans
  const sorted = [...events].sort((a, b) => (a.created_at || 0) - (b.created_at || 0));

  for (const ev of sorted) {
    if (ev.pubkey !== OPERATOR_PUBKEY) continue;

    const tags = ev.tags || [];
    const isBan = tags.some((t: string[]) => t[0] === 't' && t[1] === BAN_TAG);
    const isUnban = tags.some((t: string[]) => t[0] === 't' && t[1] === UNBAN_TAG);
    const targetPubkey = tags.find((t: string[]) => t[0] === 'p')?.[1];
    if (!targetPubkey) continue;

    if (isBan && !_bannedPubkeys.has(targetPubkey)) {
      _bannedPubkeys.add(targetPubkey);
      changed = true;
    } else if (isUnban && _bannedPubkeys.has(targetPubkey)) {
      _bannedPubkeys.delete(targetPubkey);
      changed = true;
    }
  }

  if (changed) {
    _persistBans();
    _notifyBanListeners();
  }
}

/** Add a pubkey to the local ban set immediately (before relay confirms). */
export function addLocalBan(pubkey: string) {
  _bannedPubkeys.add(pubkey);
  _persistBans();
  _notifyBanListeners();
}

/** Remove a pubkey from the local ban set immediately. */
export function removeLocalBan(pubkey: string) {
  _bannedPubkeys.delete(pubkey);
  _persistBans();
  _notifyBanListeners();
}

/** Check if a pubkey is banned site-wide. O(1), no hooks needed. */
export function isBanned(pubkey: string): boolean {
  return _bannedPubkeys.has(pubkey);
}

/** Get the full set of banned pubkeys (for the admin management UI). */
export function getBannedPubkeys(): string[] {
  return [..._bannedPubkeys];
}

/** Subscribe to ban list changes. Returns unsubscribe function. */
export function onBanListChange(listener: () => void): () => void {
  _banListeners.push(listener);
  return () => {
    _banListeners = _banListeners.filter(fn => fn !== listener);
  };
}

// ── Legacy threshold (flat count) — kept as fallback ────────────
// The weighted threshold (SPAM_FLAG_THRESHOLD from trustScore.ts)
// is the primary mechanism now. This flat count acts as a safety net
// for cases where trust levels aren't available.
export const FLAG_THRESHOLD = 3;

/** Returns true if this pubkey is immune to community flagging (the platform owner) */
export function isFlagImmune(pubkey: string): boolean {
  return isOperatorPubkey(pubkey);
}

/**
 * Given a list of flag events for a specific post,
 * return the set of unique pubkeys who flagged it.
 */
export function getFlaggers(flagEvents: any[]): Set<string> {
  const flaggers = new Set<string>();
  flagEvents.forEach(ev => {
    if (ev.kind === FLAG_KIND) flaggers.add(ev.pubkey);
  });
  return flaggers;
}

/**
 * Calculate the weighted spam score for a post based on trust levels
 * of the users who flagged it.
 *
 * @param flagEvents - flag events for this post
 * @param trustLevelLookup - optional map of pubkey → TrustLevel.
 *   If not provided, all reporters are treated as Level 0 (weight 1).
 */
export function getWeightedFlagScore(
  flagEvents: any[],
  trustLevelLookup?: Map<string, TrustLevel>,
): number {
  const seen = new Set<string>();
  let score = 0;

  for (const ev of flagEvents) {
    if (ev.kind !== FLAG_KIND) continue;
    if (seen.has(ev.pubkey)) continue;
    seen.add(ev.pubkey);

    const level = trustLevelLookup?.get(ev.pubkey) ?? 0;
    score += SPAM_REPORT_WEIGHT[level];
  }

  return score;
}

/**
 * Returns true if this post has been hidden by the community.
 * Uses the weighted scoring system when trust levels are available,
 * falls back to flat count otherwise.
 */
export function isCommunityHidden(
  flagEvents: any[],
  postAuthorPubkey: string,
  trustLevelLookup?: Map<string, TrustLevel>,
): boolean {
  if (isFlagImmune(postAuthorPubkey)) return false;

  // If trust levels are available, use weighted scoring
  if (trustLevelLookup && trustLevelLookup.size > 0) {
    return getWeightedFlagScore(flagEvents, trustLevelLookup) >= SPAM_FLAG_THRESHOLD;
  }

  // Fallback: flat count threshold
  return getFlaggers(flagEvents).size >= FLAG_THRESHOLD;
}

